import { SEO } from "../components/SEO";
import { PageHeader } from "../components/PageHeader";
import { zones } from "../data/zones";
import { IconCheck, IconPin } from "../components/Icon";
import { CTA } from "../components/sections/CTA";
import { InteractiveMap } from "../components/InteractiveMap";
import { breadcrumbJsonLd, localBusinessJsonLd } from "../lib/structured-data";
import { site } from "../lib/site";

export function Zones() {
  return (
    <>
      <SEO
        title="Zones d'intervention - Vichy, Cusset, Bellerive & Allier"
        description="Société de nettoyage basée à Vichy, nous intervenons à Cusset, Bellerive-sur-Allier, Abrest, Saint-Yorre, Creuzier, Hauterive, Le Vernet et toute l'agglomération vichyssoise."
        path="/zones-intervention"
        jsonLd={[
          localBusinessJsonLd(),
          breadcrumbJsonLd([
            { name: "Accueil", url: `${site.baseUrl}/` },
            { name: "Zones d'intervention", url: `${site.baseUrl}/zones-intervention` },
          ]),
        ]}
      />
      <PageHeader
        eyebrow="Zones d'intervention"
        title="Présents à Vichy et dans tout le bassin de l'Allier"
        subtitle="Notre siège se trouve à Vichy. Nous rayonnons dans un rayon d'environ 30 km pour couvrir l'agglomération et les principales communes de l'Allier (03)."
        breadcrumbs={[
          { label: "Accueil", to: "/" },
          { label: "Zones d'intervention" },
        ]}
      />

      <section className="section bg-surface-soft">
        <div className="container-page grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-7">
            <InteractiveMap />
          </div>
          <div className="lg:col-span-5">
            <span className="eyebrow">Notre rayon d'action</span>
            <h2 className="mt-4 text-3xl">30 km autour de Vichy</h2>
            <p className="mt-4 text-ink-muted text-lg leading-relaxed">
              Depuis notre siège à Vichy, nous intervenons en cercles concentriques :
              prioritairement en centre-ville et dans l'agglomération, puis jusqu'à 30 km
              pour couvrir le bassin vichyssois.
            </p>
            <ul className="mt-6 space-y-3">
              {[
                { km: "5 km", label: "Intervention sous 30 min, même en urgence" },
                { km: "10 km", label: "Interventions régulières quotidiennes et hebdomadaires" },
                { km: "30 km", label: "Contrats récurrents & prestations planifiées" },
              ].map((r) => (
                <li
                  key={r.km}
                  className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-4"
                >
                  <span className="grid h-9 w-9 place-items-center rounded-lg bg-brand-600 text-white flex-none text-sm font-bold">
                    {r.km}
                  </span>
                  <span className="text-sm text-ink leading-snug mt-1">{r.label}</span>
                </li>
              ))}
            </ul>
            <p className="mt-6 text-sm text-ink-muted">
              <IconCheck className="inline h-4 w-4 text-accent-600 align-[-2px] mr-1" />
              Déplacements inclus à Vichy et dans un rayon de 10 km.
            </p>
          </div>
        </div>
      </section>

      <section className="section bg-white">
        <div className="container-page">
          <header className="max-w-2xl mb-10">
            <span className="eyebrow">Communes couvertes</span>
            <h2 className="mt-4">Principales villes desservies par {site.name}</h2>
          </header>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {zones.map((z) => (
              <article
                key={z.name}
                className="card card-hover"
                itemScope
                itemType="https://schema.org/City"
              >
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 place-items-center rounded-lg bg-brand-600 text-white">
                    <IconPin className="h-5 w-5" />
                  </span>
                  <div>
                    <h2 className="text-xl" itemProp="name">
                      {z.name}
                    </h2>
                    <div className="text-xs text-ink-muted">
                      {z.postalCode} ·{" "}
                      {z.distanceKm === 0
                        ? "Siège de l'entreprise"
                        : `${z.distanceKm} km depuis Vichy`}
                    </div>
                  </div>
                </div>
                <p className="mt-4 text-sm text-ink-muted leading-relaxed">{z.description}</p>
              </article>
            ))}
          </div>

          <div className="mt-12 rounded-2xl bg-brand-50 border border-brand-100 p-6 sm:p-8 text-center">
            <h2 className="text-2xl">Votre commune n'est pas listée&nbsp;?</h2>
            <p className="mt-2 text-ink-muted">
              Contactez-nous : nous étudions chaque demande au cas par cas, y compris au-delà de
              30 km pour les contrats réguliers.
            </p>
          </div>
        </div>
      </section>

      <CTA />
    </>
  );
}
